// LamTech AI — публичный PWA для руководства: /ai
// Три экрана: Сводка, Спросить, История. Вход — обычный логин MES.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../ai/ai.css';
import Markdown from '../ai/md';
import {
  hasSession, login, logout, getBrief, sendChat, getConversations,
  getConversation, errorText, NO_ACCESS, getConsent, acceptConsent, CONSENT_REQUIRED,
  getMe, bridgeToMes,
} from '../ai/aiApi';

const SUGGESTIONS = ['Что было вчера?', 'Почему простой?', 'Статус заказа…', 'Где брак?'];

const pad = (n) => String(n).padStart(2, '0');
const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const shiftDay = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return isoDate(d); };
const hm = (min) => `${Math.floor((min || 0) / 60)}:${pad(Math.round((min || 0) % 60))}`;
const pct = (v) => `${String(Number(v || 0).toFixed(1)).replace('.', ',')}%`;
const fmtDT = (s) => {
  const d = new Date(s);
  return isNaN(d) ? '' : `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Ссылку от сервера открываем только если она ведёт на http(s) или внутрь MES.
function safeUrl(u) {
  if (typeof u !== 'string') return null;
  if (u.startsWith('/') && !u.startsWith('//')) return u;
  return /^https?:\/\//i.test(u) ? u : null;
}

function InstallBanner() {
  const [shown, setShown] = useState(() => {
    const ua = navigator.userAgent || '';
    const ios = /iPhone|iPad|iPod/.test(ua);
    const standalone = window.navigator.standalone === true
      || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
    let closed = false;
    try { closed = localStorage.getItem('ai_install_closed') === '1'; } catch (e) { /* */ }
    return ios && !standalone && !closed;
  });
  if (!shown) return null;
  const close = () => {
    try { localStorage.setItem('ai_install_closed', '1'); } catch (e) { /* */ }
    setShown(false);
  };
  return (
    <div className="ai-install">
      <p><b>Чтобы открывать одним касанием:</b> нажмите «Поделиться» (квадрат со стрелкой внизу Safari) → «На экран Домой».</p>
      <button className="ai-x" onClick={close} aria-label="Закрыть">×</button>
    </div>
  );
}

function NoAccess({ onExit }) {
  return (
    <div className="ai-body">
      <div className="ai-login">
        <h1>LamTech AI</h1>
        <div className="ai-err">{NO_ACCESS}</div>
        <p className="ai-muted">ИИ-помощник доступен руководству. Войдите под другим логином.</p>
        <button className="ai-btn pri" style={{ width: '100%', marginTop: 8 }} onClick={onExit}>Выйти</button>
      </div>
    </div>
  );
}

// Политика конфиденциальности: без согласия ИИ не работает (сервер отвечает 403 consent_required).
function Consent({ onAccepted, onAuthLost, onDenied, onExit }) {
  const [c, setC] = useState(null);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let dead = false;
    getConsent()
      .then(d => { if (dead) return; if (d.accepted) onAccepted(); else setC(d); })
      .catch(e => { if (dead) return; if (e.status === 401) onAuthLost(); else if (e.status === 403) onDenied(e); else setErr(errorText(e)); });
    return () => { dead = true; };
  }, [onAccepted, onAuthLost, onDenied]);
  const accept = async () => {
    setBusy(true); setErr('');
    try { await acceptConsent(c.version); onAccepted(); }
    catch (e) { if (e.status === 401) onAuthLost(); else setErr(errorText(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="ai-body">
      <div className="ai-login">
        <h1>Политика конфиденциальности</h1>
        {err && <div className="ai-err">{err}</div>}
        {!c && !err && <p className="ai-muted">Загружаю…</p>}
        {c && (
          <>
            <div className="ai-policy" tabIndex={0}>{c.text}</div>
            <label className="ai-agree">
              <input type="checkbox" checked={ok} onChange={e => setOk(e.target.checked)} />
              <span>Я ознакомлен(а) и согласен(на)</span>
            </label>
            <button className="ai-btn pri" style={{ width: '100%', marginTop: 8 }}
                    disabled={!ok || busy} onClick={accept}>{busy ? 'Сохраняю…' : 'Начать работу'}</button>
          </>
        )}
        <button className="ai-btn" style={{ width: '100%', marginTop: 8 }} onClick={onExit}>Выйти</button>
      </div>
    </div>
  );
}

function Login({ onDone }) {
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try { await login(u.trim(), p); onDone(); }
    catch (x) { setErr(errorText(x)); }
    finally { setBusy(false); }
  };
  return (
    <div className="ai-body">
      <form className="ai-login" onSubmit={submit}>
        <h1>LamTech AI</h1>
        <p className="ai-muted">Войдите своим логином от MES. Пароль больше не спросим — вход запоминается.</p>
        {err && <div className="ai-err">{err}</div>}
        <input className="ai-field" placeholder="Логин" value={u} autoCapitalize="none"
               autoCorrect="off" autoComplete="username" onChange={e => setU(e.target.value)} />
        <input className="ai-field" type="password" placeholder="Пароль" value={p}
               autoComplete="current-password" onChange={e => setP(e.target.value)} />
        <button className="ai-btn pri" style={{ width: '100%', marginTop: 8 }}
                disabled={busy || !u || !p}>{busy ? 'Входим…' : 'Войти'}</button>
      </form>
    </div>
  );
}

function Brief({ onAuthLost, onDenied }) {
  const [date, setDate] = useState(shiftDay(-1));
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let dead = false;
    setLoading(true); setErr('');
    getBrief(date)
      .then(d => { if (!dead) setData(d); })
      .catch(e => { if (dead) return; if (e.status === 401) onAuthLost(); else if (e.status === 403) onDenied(e); else { setData(null); setErr(errorText(e)); } })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [date, onAuthLost, onDenied]);

  const yest = shiftDay(-1), today = shiftDay(0);
  return (
    <div className="ai-body">
      <div className="ai-days">
        <button className={`ai-btn ai-day ${date === yest ? 'on' : ''}`} onClick={() => setDate(yest)}>Вчера</button>
        <button className={`ai-btn ai-day ${date === today ? 'on' : ''}`} onClick={() => setDate(today)}>Сегодня</button>
        <input className="ai-date" type="date" value={date} max={today}
               onChange={e => e.target.value && setDate(e.target.value)} aria-label="Дата" />
      </div>
      {err && <div className="ai-err">{err}</div>}
      {loading && !data && <p className="ai-muted">Загружаю…</p>}
      {data && (
        <>
          {data.summary_text && <div className="ai-card ai-sum"><Markdown text={data.summary_text} /></div>}
          {(data.branches || []).map(b => (
            <div className="ai-card" key={b.id} style={loading ? { opacity: .6 } : null}>
              <div className="ai-bhead">
                <span className={`ai-dot ${b.status}`} title={b.status} />
                <span className="ai-bname">{b.name}</span>
              </div>
              <div className="ai-stats">
                <div className="ai-stat"><b>{b.sheets}</b><span>листов</span></div>
                <div className="ai-stat"><b>{pct(b.grade1_pct)}</b><span>1-й сорт</span></div>
                <div className="ai-stat"><b>{pct(b.defect_pct)}</b><span>брак</span></div>
                <div className="ai-stat"><b>{hm(b.downtime_min)}</b><span>простой (ч:мин)</span></div>
              </div>
              {b.note && <div className="ai-note">{b.note}</div>}
            </div>
          ))}
          {data.branches && data.branches.length === 0 && <p className="ai-muted">За эту дату данных нет.</p>}
          {data.generated_at && <p className="ai-muted">Обновлено {fmtDT(data.generated_at)}</p>}
        </>
      )}
    </div>
  );
}

// Сводка для инвестора: по карточке на цех — выпуск, брак, простой, светофор.
// Без таблиц и текста от ИИ; подробности — вопросом к ИИ (кнопка и готовые вопросы).
const STATUS_TEXT = { ok: 'Всё в норме', warn: 'Есть отклонения', bad: 'Нужно внимание' };

function InvestorBrief({ onAuthLost, onDenied, onAsk }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let dead = false;
    Promise.all([getBrief(shiftDay(0), false), getBrief(shiftDay(-1), false)])
      .then(([today, yest]) => { if (!dead) setData({ today, yest }); })
      .catch(e => { if (dead) return; if (e.status === 401) onAuthLost(); else if (e.status === 403) onDenied(e); else setErr(errorText(e)); });
    return () => { dead = true; };
  }, [onAuthLost, onDenied]);

  const todayBy = {};
  ((data && data.today.branches) || []).forEach(b => { todayBy[b.id] = b; });
  const rows = ((data && data.yest.branches) || []);
  const questions = [];
  rows.filter(b => b.status !== 'ok').slice(0, 2).forEach(b => {
    questions.push(`Почему у цеха ${b.name} ${b.status === 'bad' ? 'красный' : 'жёлтый'} светофор?`);
  });
  questions.push('Сколько выпустили за неделю?', 'Где больше всего брака?');

  return (
    <div className="ai-body">
      {err && <div className="ai-err">{err}</div>}
      {!data && !err && <p className="ai-muted">Загружаю…</p>}
      {data && rows.map(b => {
        const t = todayBy[b.id] || {};
        return (
          <div className="ai-card" key={b.id}>
            <div className="ai-bhead">
              <span className={`ai-dot ${b.status}`} title={b.status} />
              <span className="ai-bname">{b.name}</span>
              <span className="ai-muted ai-inv-st">{STATUS_TEXT[b.status] || ''}</span>
            </div>
            <div className="ai-stats">
              <div className="ai-stat"><b>{t.sheets ?? 0}</b><span>выпуск сегодня, листов</span></div>
              <div className="ai-stat"><b>{b.sheets}</b><span>выпуск вчера, листов</span></div>
              <div className="ai-stat"><b>{pct(b.defect_pct)}</b><span>брак вчера</span></div>
              <div className="ai-stat"><b>{hm(b.downtime_min)}</b><span>простой вчера (ч:мин)</span></div>
            </div>
            {b.note && <div className="ai-note">{b.note}</div>}
          </div>
        );
      })}
      {data && rows.length === 0 && <p className="ai-muted">Данных по цехам нет.</p>}
      {data && (
        <>
          <button className="ai-btn pri ai-inv-ask" onClick={() => onAsk(null)}>Спросите у ИИ</button>
          <div className="ai-chips ai-inv-q">
            {questions.map(q => (
              <button key={q} className="ai-chip" onClick={() => onAsk(q)}>{q}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Внутренняя ссылка (/reports…) — в этом же окне через роутер: в iPhone-PWA
// target=_blank открывает Safari, и вход теряется. ?from=ai включает на экране
// кнопку «← Назад к ИИ» (ai/AiBackBar.js).
const isInternal = (u) => u.startsWith('/') && !u.startsWith('//');
const withFromAi = (u) => u + (u.includes('?') ? '&' : '?') + 'from=ai';

function Bubble({ m }) {
  const navigate = useNavigate();
  if (m.role === 'user') return <div className="ai-bub user">{m.text}</div>;
  const links = (m.links || []).map(l => ({ ...l, url: safeUrl(l.url) })).filter(l => l.url);
  const open = async (e, url) => {
    e.preventDefault();
    await bridgeToMes();
    navigate(withFromAi(url));
  };
  return (
    <div className="ai-bub bot">
      {m.error ? <div>{m.text}</div> : <Markdown text={m.text} />}
      {links.map((l, i) => (
        isInternal(l.url)
          ? <a key={i} className="ai-lnk" href={withFromAi(l.url)} onClick={e => open(e, l.url)}>
            {l.title || 'Открыть подробно'}
          </a>
          : <a key={i} className="ai-lnk" href={l.url} target="_blank" rel="noopener noreferrer">
            {l.title || 'Открыть подробно'}
          </a>
      ))}
      {(m.caveats || []).map((c, i) => <div key={i} className="ai-cav">{c}</div>)}
    </div>
  );
}

function Chat({ chat, setChat, onAuthLost, onDenied, pending, onPendingUsed }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const { messages, convId } = chat;

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ block: 'end' });
  }, [messages, busy]);

  const send = async (q) => {
    const msg = (q ?? text).trim();
    if (!msg || busy) return;
    setText('');
    setBusy(true);
    setChat(c => ({ ...c, messages: [...c.messages, { role: 'user', text: msg }] }));
    try {
      const r = await sendChat(msg, convId);
      setChat(c => ({
        convId: r.conversation_id || c.convId,
        messages: [...c.messages, { role: 'bot', text: r.answer, links: r.links, caveats: r.caveats }],
      }));
    } catch (e) {
      if (e.status === 401) { onAuthLost(); return; }
      if (e.status === 403) { onDenied(e); return; }
      setChat(c => ({ ...c, messages: [...c.messages, { role: 'bot', text: errorText(e), error: true }] }));
    } finally { setBusy(false); }
  };

  // Готовый вопрос из сводки: отправляем один раз при открытии вкладки.
  useEffect(() => {
    if (pending && !busy) { onPendingUsed(); send(pending); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  return (
    <div className="ai-chat">
      <div className="ai-msgs">
        {messages.length === 0 && (
          <p className="ai-muted">Задайте вопрос по заводу обычными словами — или нажмите подсказку ниже.</p>
        )}
        {messages.map((m, i) => <Bubble key={i} m={m} />)}
        {busy && <div className="ai-bub bot ai-think">Думаю<i>.</i><i>.</i><i>.</i> (до минуты)</div>}
        <div ref={endRef} />
      </div>
      <div className="ai-chips">
        {SUGGESTIONS.map(s => (
          <button key={s} className="ai-chip" disabled={busy}
                  onClick={() => (s.endsWith('…') ? setText(s.slice(0, -1) + ' ') : send(s))}>{s}</button>
        ))}
      </div>
      <form className="ai-compose" onSubmit={e => { e.preventDefault(); send(); }}>
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Ваш вопрос…"
               enterKeyHint="send" autoComplete="off" aria-label="Вопрос" />
        <button className="ai-send" disabled={busy || !text.trim()} aria-label="Отправить">➤</button>
      </form>
      <div className="ai-hint">Можно говорить голосом — кнопка микрофона на клавиатуре.</div>
    </div>
  );
}

function History({ onOpen, onAuthLost, onDenied }) {
  const [list, setList] = useState(null);
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState(null);
  useEffect(() => {
    let dead = false;
    getConversations()
      .then(d => { if (!dead) setList(d); })
      .catch(e => { if (dead) return; if (e.status === 401) onAuthLost(); else if (e.status === 403) onDenied(e); else setErr(errorText(e)); });
    return () => { dead = true; };
  }, [onAuthLost, onDenied]);
  const open = async (id) => {
    setBusyId(id); setErr('');
    try { onOpen(await getConversation(id)); }
    catch (e) { if (e.status === 401) onAuthLost(); else setErr(errorText(e)); }
    finally { setBusyId(null); }
  };
  return (
    <div className="ai-body">
      {err && <div className="ai-err">{err}</div>}
      {!list && !err && <p className="ai-muted">Загружаю…</p>}
      {list && list.length === 0 && <p className="ai-muted">Вопросов пока не было.</p>}
      {(list || []).map(c => (
        <button key={c.id} className="ai-row" onClick={() => open(c.id)} disabled={busyId === c.id}>
          <b>{c.title || `Разговор ${c.id}`}</b>
          <span className="ai-muted">{fmtDT(c.created_at)}</span>
        </button>
      ))}
    </div>
  );
}

export default function AiApp() {
  const [authed, setAuthed] = useState(hasSession());
  // Вкладка и разговор живут в sessionStorage: отчёт открывается в этом же окне
  // (ссылка от ИИ), и по «← Назад к ИИ» человек должен вернуться к своему вопросу.
  const [tab, setTabState] = useState(() => {
    try { return sessionStorage.getItem('ai_tab') || 'brief'; } catch (e) { return 'brief'; }
  });
  const setTab = useCallback((t) => {
    setTabState(t);
    try { sessionStorage.setItem('ai_tab', t); } catch (e) { /* */ }
  }, []);
  const [chat, setChat] = useState(() => {
    try {
      const c = JSON.parse(sessionStorage.getItem('ai_chat') || 'null');
      if (c && Array.isArray(c.messages)) return c;
    } catch (e) { /* */ }
    return { convId: null, messages: [] };
  });
  useEffect(() => {
    try { sessionStorage.setItem('ai_chat', JSON.stringify(chat)); } catch (e) { /* */ }
  }, [chat]);
  const [role, setRole] = useState(null);
  const [pending, setPending] = useState(null);
  const askAi = (q) => { setPending(q); setTab('chat'); };

  // Высота по видимой области: поле ввода не уезжает под клавиатуру.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const set = () => document.documentElement.style.setProperty('--ai-vh', `${vv.height}px`);
    set();
    vv.addEventListener('resize', set);
    return () => { vv.removeEventListener('resize', set); document.documentElement.style.removeProperty('--ai-vh'); };
  }, []);

  const [denied, setDenied] = useState(false);
  // null — ещё не знаем; true — нужно принять политику; false — принята
  const [needConsent, setNeedConsent] = useState(null);
  const onAuthLost = useCallback(() => {
    logout(); setAuthed(false); setDenied(false); setNeedConsent(null); setRole(null);
    try { sessionStorage.removeItem('ai_tab'); } catch (e) { /* */ }
    setChat({ convId: null, messages: [] });
  }, []);
  const onDenied = useCallback((e) => {
    if (e && e.code === CONSENT_REQUIRED) setNeedConsent(true); else setDenied(true);
  }, []);
  const onAccepted = useCallback(() => setNeedConsent(false), []);

  // Роль нужна, чтобы выбрать вид сводки (инвестору — простая).
  useEffect(() => {
    if (!authed || needConsent !== false || role !== null) return undefined;
    let dead = false;
    getMe().then(m => { if (!dead) setRole((m && m.role) || ''); })
      .catch(e => { if (!dead) { if (e.status === 401) onAuthLost(); else setRole(''); } });
    return () => { dead = true; };
  }, [authed, needConsent, role, onAuthLost]);

  const openConv = (c) => {
    setChat({
      convId: c.id,
      messages: (c.messages || []).map(m => (m.role === 'user'
        ? { role: 'user', text: m.content }
        : {
          role: 'bot', links: m.links, caveats: m.caveats,
          error: !!m.error, text: m.error ? 'Не удалось получить ответ на этот вопрос.' : m.content,
        })),
    });
    setTab('chat');
  };

  return (
    <div className="ai-root">
      <InstallBanner />
      <div className="ai-head">
        <span className="ai-title">LamTech AI</span>
        {authed && (
          <span>
            {tab === 'chat' && chat.messages.length > 0 && (
              <button className="ai-link" onClick={() => setChat({ convId: null, messages: [] })}>Новый вопрос</button>
            )}
            <button className="ai-link" onClick={onAuthLost}>Выйти</button>
          </span>
        )}
      </div>
      {!authed ? <Login onDone={() => setAuthed(true)} /> : denied ? <NoAccess onExit={onAuthLost} /> : needConsent !== false ? (
        <Consent onAccepted={onAccepted} onAuthLost={onAuthLost} onDenied={onDenied} onExit={onAuthLost} />
      ) : (
        <>
          {tab === 'brief' && role === null && <div className="ai-body"><p className="ai-muted">Загружаю…</p></div>}
          {tab === 'brief' && role === 'investor' && <InvestorBrief onAuthLost={onAuthLost} onDenied={onDenied} onAsk={askAi} />}
          {tab === 'brief' && role !== null && role !== 'investor' && <Brief onAuthLost={onAuthLost} onDenied={onDenied} />}
          {tab === 'chat' && <Chat chat={chat} setChat={setChat} onAuthLost={onAuthLost} onDenied={onDenied}
                                   pending={pending} onPendingUsed={() => setPending(null)} />}
          {tab === 'history' && <History onOpen={openConv} onAuthLost={onAuthLost} onDenied={onDenied} />}
          <nav className="ai-tabs">
            {[['brief', 'Сводка'], ['chat', 'Спросить'], ['history', 'История']].map(([k, t]) => (
              <button key={k} className={`ai-tab ${tab === k ? 'on' : ''}`} onClick={() => setTab(k)}>{t}</button>
            ))}
          </nav>
        </>
      )}
    </div>
  );
}
